import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { WaziWordmark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PhotoUpload from "../components/PhotoUpload";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, FileText, Briefcase, Star, LogOut,
  Loader2, ExternalLink, Copy, Share2, Gift, MapPin, MessageCircle, Save,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dedicated axios instance for worker auth (avoids redirecting to admin login on 401)
const wapi = axios.create({ baseURL: API });
wapi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("wazi_worker_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "profile", label: "My profile", icon: Save },
];

const WorkerDashboard = () => {
  const nav = useNavigate();
  const [worker, setWorker] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [items, setItems] = useState({ leads: [], quotes: [], jobs: [], reviews: [] });

  const logout = () => {
    localStorage.removeItem("wazi_worker_token");
    localStorage.removeItem("wazi_worker");
    nav("/worker/login");
  };

  useEffect(() => {
    const t = localStorage.getItem("wazi_worker_token");
    if (!t) { nav("/worker/login"); return; }
    Promise.all([
      wapi.get("/worker/me"),
      wapi.get("/worker/stats"),
    ]).then(([me, s]) => {
      setWorker(me.data);
      setStats(s.data);
    }).catch(() => {
      localStorage.removeItem("wazi_worker_token");
      nav("/worker/login");
    });
  }, [nav]);

  // load tab data on demand
  useEffect(() => {
    if (!worker) return;
    if (tab === "overview") return;
    const map = { leads: "/worker/leads", quotes: "/worker/quotes", jobs: "/worker/jobs", reviews: "/worker/reviews" };
    const url = map[tab];
    if (!url) return;
    wapi.get(url).then((r) => setItems((s) => ({ ...s, [tab]: r.data.items }))).catch(() => {});
  }, [tab, worker]);

  if (!worker) return <div className="min-h-screen flex items-center justify-center text-[#718096]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 bg-white border-r border-[#E2E8F0] md:flex md:flex-col">
        <Link to="/" className="p-6 border-b border-[#E2E8F0] hidden md:block"><WaziWordmark size={28} /></Link>
        <nav className="flex md:flex-col overflow-x-auto md:overflow-visible p-3 gap-1 md:gap-0 md:space-y-1 border-b border-[#E2E8F0] md:border-b-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`worker-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                tab === t.id ? "bg-[#25D366]/10 text-[#16A34A]" : "text-[#2D3748] hover:bg-[#F7FAFC]"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="hidden md:block mt-auto p-4 border-t border-[#E2E8F0]">
          <div className="flex items-center gap-3 mb-3">
            {worker.avatar_url ? (
              <img src={worker.avatar_url} className="w-9 h-9 rounded-full object-cover" alt="" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#25D366]/10 text-[#25D366] flex items-center justify-center font-heading font-semibold">
                {(worker.business_name || "")[0]}
              </div>
            )}
            <div className="text-sm min-w-0">
              <div className="font-heading font-semibold text-[#2D3748] truncate">{worker.business_name}</div>
              <div className="text-xs text-[#718096] truncate">{worker.whatsapp_number}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="worker-logout-btn" className="w-full flex items-center justify-center gap-2 text-sm text-[#DC2626] py-2 rounded-lg hover:bg-[#DC2626]/5">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 md:p-8 min-w-0">
        {tab === "overview" && <Overview worker={worker} stats={stats} />}
        {tab === "leads" && <LeadsTab items={items.leads} />}
        {tab === "quotes" && <QuotesTab items={items.quotes} />}
        {tab === "jobs" && <JobsTab items={items.jobs} />}
        {tab === "reviews" && <ReviewsTab items={items.reviews} />}
        {tab === "profile" && <ProfileTab worker={worker} onSaved={setWorker} />}
      </main>
    </div>
  );
};

const KPI = ({ label, value, color = "bg-[#25D366]", icon: Icon, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 lift-on-hover" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}><Icon className="w-5 h-5 text-white" /></div>
    <div className="mt-4 text-3xl font-heading font-bold text-[#2D3748]">{value}</div>
    <div className="text-sm text-[#718096]">{label}</div>
  </div>
);

const Overview = ({ worker, stats }) => {
  const referLink = `${window.location.origin}/u/${worker.slug}?ref=${worker.referral_code || ""}`;
  const copyRef = () => {
    try { navigator.clipboard.writeText(referLink); toast.success("Link copied!"); }
    catch { toast.error("Couldn't copy"); }
  };
  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#2D3748]">Welcome back, {worker.full_name.split(" ")[0]} 👋</h1>
      <p className="text-[#718096] mt-1">Your business at a glance.</p>
      {stats && (
        <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <KPI testid="w-kpi-leads" label="Leads matched" value={stats.leads} icon={Inbox} color="bg-[#1E88E5]" />
          <KPI testid="w-kpi-quotes" label="Quotes sent" value={stats.quotes} icon={FileText} color="bg-[#FFB400]" />
          <KPI testid="w-kpi-jobs-active" label="Active jobs" value={stats.jobs_active} icon={Briefcase} color="bg-[#25D366]" />
          <KPI testid="w-kpi-jobs-done" label="Jobs completed" value={stats.jobs_completed} icon={Briefcase} color="bg-[#16A34A]" />
          <KPI testid="w-kpi-rating" label={`Reviews · ⭐ ${(stats.rating_avg || 0).toFixed(1)}`} value={stats.reviews_count} icon={Star} color="bg-[#FFB400]" />
          <KPI testid="w-kpi-airtime" label={`Airtime earned (refs)`} value={`R${(stats.airtime_earned_zar || 0).toFixed(0)}`} icon={Gift} color="bg-[#1E88E5]" />
        </div>
      )}
      {/* Referral card */}
      <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#16A34A] text-white p-6">
        <div className="flex items-center gap-2 text-white/90 text-xs uppercase font-heading font-semibold tracking-widest">
          <Gift className="w-3.5 h-3.5" /> Refer & earn airtime
        </div>
        <h3 className="font-heading text-xl font-bold mt-2">Your code: {worker.referral_code}</h3>
        <p className="mt-1 text-white/85 text-sm">Share this link — get R20 airtime for every painter who joins.</p>
        <div className="mt-3 p-3 bg-white/15 backdrop-blur rounded-xl text-sm break-all font-mono">{referLink}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={copyRef} data-testid="overview-copy-ref" variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#16A34A]">
            <Copy className="w-4 h-4 mr-2" /> Copy link
          </Button>
          <a href={`https://wa.me/?text=${encodeURIComponent(`I use Wazi to manage my painting business — leads, quotes, reviews, all on WhatsApp. Join with my link: ${referLink}`)}`} target="_blank" rel="noopener noreferrer">
            <Button data-testid="overview-share-wa" className="rounded-full bg-white text-[#16A34A] hover:bg-white/90">
              <Share2 className="w-4 h-4 mr-2" /> Share on WhatsApp
            </Button>
          </a>
          <Link to={`/u/${worker.slug}`} target="_blank">
            <Button variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#16A34A]" data-testid="overview-view-profile">
              <ExternalLink className="w-4 h-4 mr-2" /> View my public profile
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const TabHeader = ({ title, subtitle }) => (
  <div className="mb-6">
    <h1 className="font-heading text-3xl font-bold text-[#2D3748]">{title}</h1>
    {subtitle && <p className="text-[#718096] mt-1">{subtitle}</p>}
  </div>
);

const Empty = ({ children }) => <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center text-[#718096]">{children}</div>;

const LeadsTab = ({ items }) => (
  <div>
    <TabHeader title="Leads matched to you" subtitle="Customers in your service area who need a painter." />
    {items.length === 0 ? <Empty>No leads yet. Share your profile link to attract more!</Empty> : (
      <ul className="space-y-3">
        {items.map((l) => (
          <li key={l.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`w-lead-${l.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="font-heading font-semibold text-[#2D3748]">{l.customer_name} <span className="text-[#718096] font-normal"><MapPin className="inline w-3.5 h-3.5" /> {l.location}</span></div>
                <p className="text-sm text-[#2D3748] mt-1">{l.description}</p>
                <div className="text-xs text-[#718096] mt-1">{l.interior_exterior || "—"} {l.timeline ? `· ${l.timeline}` : ""}</div>
                {l.photos?.length > 0 && (
                  <div className="mt-2 flex gap-1.5">
                    {l.photos.slice(0, 4).map((p, i) => <img key={i} src={p} alt="" className="w-12 h-12 rounded-md object-cover border border-[#E2E8F0]" />)}
                  </div>
                )}
              </div>
              <a href={`https://wa.me/${(l.customer_whatsapp || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${l.customer_name.split(" ")[0]}, this is regarding your painting request on Wazi`)}`} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white"><MessageCircle className="w-3 h-3 mr-1" /> WhatsApp</Button>
              </a>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const QuotesTab = ({ items }) => (
  <div>
    <TabHeader title="My quotes" subtitle="Quotes you've sent and their status." />
    {items.length === 0 ? <Empty>No quotes yet.</Empty> : (
      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7FAFC] text-[#718096] uppercase tracking-wider text-xs">
            <tr><th className="text-left px-5 py-3">#</th><th className="text-left px-5 py-3">Customer</th><th className="text-right px-5 py-3">Total</th><th className="text-left px-5 py-3">Status</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {items.map((q) => (
              <tr key={q.id} data-testid={`w-quote-${q.quote_number}`}>
                <td className="px-5 py-3 font-mono text-xs text-[#718096]">{q.quote_number}</td>
                <td className="px-5 py-3 text-[#2D3748] font-medium">{q.customer_name}</td>
                <td className="px-5 py-3 text-right font-heading font-semibold">R {q.total.toFixed(2)}</td>
                <td className="px-5 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${q.status === "approved" ? "bg-[#16A34A]/10 text-[#16A34A]" : q.status === "sent" ? "bg-[#1E88E5]/10 text-[#1E88E5]" : q.status === "rejected" ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-[#F7FAFC] text-[#718096]"}`}>{q.status}</span></td>
                <td className="px-5 py-3 text-right">
                  <a href={`${API}/public/quotes/${q.id}/pdf`} target="_blank" rel="noopener noreferrer" className="text-[#1E88E5] hover:underline text-xs">PDF →</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const JobsTab = ({ items }) => (
  <div>
    <TabHeader title="My jobs" subtitle="Active and completed work." />
    {items.length === 0 ? <Empty>No jobs yet — they appear here when a customer approves your quote.</Empty> : (
      <ul className="space-y-3">
        {items.map((j) => (
          <li key={j.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5 flex items-center justify-between" data-testid={`w-job-${j.id}`}>
            <div>
              <div className="font-heading font-semibold text-[#2D3748]">Job #{j.id.slice(0, 6)}</div>
              <div className="text-xs text-[#718096] mt-1">Created {new Date(j.created_at).toLocaleDateString()} {j.completed_at && `· Completed ${new Date(j.completed_at).toLocaleDateString()}`}</div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${j.status === "completed" ? "bg-[#16A34A]/10 text-[#16A34A]" : j.status === "in_progress" ? "bg-[#FFB400]/10 text-[#FFB400]" : j.status === "cancelled" ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-[#1E88E5]/10 text-[#1E88E5]"}`}>{j.status.replace("_", " ")}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ReviewsTab = ({ items }) => (
  <div>
    <TabHeader title="My reviews" subtitle="What your customers say." />
    {items.length === 0 ? <Empty>No reviews yet — complete a job and your customer will be asked automatically.</Empty> : (
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`w-review-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-[#2D3748]">{r.customer_name}</div>
              <div className="inline-flex">{[1,2,3,4,5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= r.stars ? "text-[#FFB400] fill-[#FFB400]" : "text-[#E2E8F0]"}`} />)}</div>
            </div>
            <p className="mt-2 text-sm text-[#2D3748]">{r.text}</p>
            <div className="mt-2 text-xs text-[#718096]">{new Date(r.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ProfileTab = ({ worker, onSaved }) => {
  const [form, setForm] = useState({
    business_name: worker.business_name || "",
    description: worker.description || "",
    service_areas: (worker.service_areas || []).join(", "),
    languages: (worker.languages || []).join(", "),
    avatar_url: worker.avatar_url || "",
  });
  const [gallery, setGallery] = useState((worker.gallery || []).map((u, i) => ({ url: u, path: `existing-${i}` })));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        business_name: form.business_name,
        description: form.description,
        service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        avatar_url: form.avatar_url || null,
        gallery: gallery.map((g) => g.url),
      };
      const res = await wapi.patch("/worker/profile", payload);
      onSaved(res.data);
      toast.success("Profile updated");
    } catch {
      toast.error("Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <TabHeader title="My profile" subtitle="What customers see on your public page." />
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 max-w-2xl space-y-5">
        <div>
          <Label>Business name</Label>
          <Input data-testid="p-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label>One-sentence description</Label>
          <textarea data-testid="p-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#25D366]" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Service areas <span className="text-xs text-[#718096]">(comma-separated)</span></Label>
            <Input data-testid="p-areas" value={form.service_areas} onChange={(e) => setForm({ ...form, service_areas: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Durbanville, Bellville" />
          </div>
          <div>
            <Label>Languages</Label>
            <Input data-testid="p-langs" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className="mt-1.5 rounded-xl" placeholder="English, isiZulu" />
          </div>
        </div>
        <div>
          <Label>Avatar URL</Label>
          <Input data-testid="p-avatar" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="mt-1.5 rounded-xl" placeholder="https://… (optional)" />
        </div>
        <div>
          <Label>Portfolio gallery</Label>
          <p className="text-xs text-[#718096] mt-0.5 mb-2">Show off your best work — these appear on your public profile.</p>
          <PhotoUpload value={gallery} onChange={setGallery} max={12} />
        </div>
        <Button onClick={save} disabled={busy} data-testid="p-save" className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white px-6">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save changes
        </Button>
      </div>
    </div>
  );
};

export default WorkerDashboard;
